import { Box, Grow, List, ListItem, Popover, Slide } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useCallback, useContext, useEffect } from 'react'
import { GoogleAd } from '../..'
import { useGoogleAdsense } from '../../../hooks'
import { AppbarActionContext } from '../context'
import useActions from '../hooks/useActions'
import useAppbarEvent from '../hooks/useAppbarEvent'
import useFormFactor from '../hooks/useFormFactor'
import { appBarHeight } from '../style'
import CloseButton from './close-button'
import MenuButtonBar from './menu-button-bar'

// The !important styles are needed to override inline style attribute values.
const useStyles = makeStyles(({ spacing, breakpoints }) => ({
  paper: ({ bps }) => ({
    top: '0 !important',
    [breakpoints.down(bps.smallDesktop)]: {
      maxHeight: '100%',
      maxWidth: '100%',
      left: 'calc(100vw / 2) !important',
      right: 0,
      bottom: 0
    },
    [breakpoints.down(bps.tablet)]: {
      left: '0 !important'
    },
    [breakpoints.up(bps.smallDesktop)]: {
      padding: 0
    }
  }),
  list: {
    minWidth: 'fit-content',
    width: '100%'
  },
  listItem: {
    marginBottom: '8px',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    '&:last-child': {
      marginBottom: 0
    },
    '& .MuiGrid-item': {
      width: '100%',
      '& > *': {
        width: '100%',
        justifyContent: 'flex-start'
      }
    },
    '& > *': {
      width: '100%'
    }
  },
  container: ({ bps }) => ({
    [breakpoints.down(bps.smallDesktop)]: {
      marginLeft: spacing(3),
      marginRight: spacing(3)
    },
    [breakpoints.down('sm')]: {
      marginLeft: spacing(2),
      marginRight: spacing(2)
    }
  }),
  popoverRoot: {
    top: `${appBarHeight}px !important`
  }
}))

const Menu = props => {
  const { formFactor, breakpoints } = useFormFactor()
  const classes = useStyles({ bps: breakpoints })
  const { filterActions } = useActions()

  const handleListClick = useCallback(args => props.onClose(args), [props])

  /*
   * The modal closing will reset the focus to either the initiating button or
   * the document body. This focuses the search if it was the cause of the
   * modal being closed.
   */
  const handleAppbarClick = useCallback(
    evt => {
      handleListClick(evt)
      if (['INPUT'].includes(evt.target.tagName)) {
        setTimeout(() => evt.target.focus(), 0)
      }
    },
    [handleListClick]
  )

  useAppbarEvent({ open: props.open, onClick: handleAppbarClick })

  const actionContext = useContext(AppbarActionContext)

  useEffect(() => {
    actionContext.onMenuShow(props.open)
  }, [props.open, actionContext])

  const isLargeMenu = ['mobile', 'tablet'].includes(formFactor)
  const paperProps = {
    classes: { root: classes.paper },
    ...(isLargeMenu && {
      variant: 'outlined',
      square: true
    })
  }

  const { useAdsense } = useGoogleAdsense()

  // disableScrollLock prevents the removal of the scrollbar from shifting the content
  // to the right. At the cost of being able to scroll the content under the modal.
  return (
    <Popover
      {...props}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}
      disableScrollLock
      TransitionComponent={isLargeMenu ? Slide : Grow}
      TransitionProps={isLargeMenu ? { direction: 'left' } : {}}
      PaperProps={paperProps}
      BackdropProps={{ style: { top: appBarHeight } }}
      classes={{ root: classes.popoverRoot }}
    >
      <div onClick={handleListClick} className={classes.container}>
        {formFactor === 'tablet' && <CloseButton onClick={handleListClick} />}
        <MenuButtonBar context='startmenubuttons' />
        <List
          className={classes.list}
          disablePadding
          sx={{
            ...(formFactor !== 'mobile' && formFactor !== 'tablet' && {
              padding: '8px !important',
              paddingTop: '8px !important',
              paddingRight: '8px !important',
              paddingBottom: '8px !important',
              paddingLeft: '8px !important'
            })
          }}
        >
          {filterActions(props.filterContext).map((action, index, arr) => {
            const actionId = actionContext.getId(action)
            const nextAction = arr[index + 1]
            const nextActionId = nextAction ? actionContext.getId(nextAction) : null
            const sectionLabelIds = ['admin-tools-label', 'logout-section']
            const isSectionLabel = sectionLabelIds.includes(actionId)
            const nextIsSectionLabel = sectionLabelIds.includes(nextActionId)
            const shouldShowDivider = ['mobile', 'tablet'].includes(formFactor) &&
                                      !isSectionLabel &&
                                      !nextIsSectionLabel

            return (
              <ListItem
                disableGutters
                key={actionId}
                onClick={evt => actionContext.onClick(evt, action)}
                divider={shouldShowDivider}
                className={classes.listItem}
              >
                {action}
              </ListItem>
            )
          })}
        </List>
        <MenuButtonBar context='endmenubuttons' />
        {(formFactor === 'mobile' && useAdsense) &&
          <Box minWidth='300px'>
            <GoogleAd
              adKey='app-toolbar'
              adPosition='menu-button'
              adSource='components>app-toolbar>menu-button>menu.js'
            />
          </Box>}
      </div>
    </Popover>
  )
}

export default Menu
